<template>
        <!-- Origin Account Dropdown Section -->
        <!-- Divider -->
        <divider-generic
        :applyBorder="false"
        :dividerSize="'medium'"
        :icon="'person'"
        :title="t('transfers.internationalCorporate.step1.dividers.originAccount')"/>
        <!-- Account Dropdown -->
        <dropdown-accounts
        :params="originAccountParams"
        :options="filteredAccountsBalances"
        :selectedOption="formData.originAccount"
        @clicked="originAccountDropdownClickHandler"/>
        <!-- Email input -->
        <input-generic
        id="ownEmail"
        :params="emailInputParams"
        :inputValue="formData.ownEmail"
        @input="[emit('updateFormData', { field: 'ownEmail', inputValue: $event.trim() }), emailInputParams.errorState = false, clearFieldError('ownEmail')]"
        @onBlur="emailValidation"/>
        <!-- Operation Data Section -->
        <!-- Divider -->
        <divider-generic
        :dividerSize="'medium'"
        :icon="'document'"
        :title="t('transfers.internationalCorporate.step1.dividers.operationData')"/>
        <!-- Operations Types Inputs -->
        <operation-type-section
        :formData="formData"
        :showInputsErrors="showInputsErrors"
        :fieldsWithError="fieldsWithError"
        :clearForm="clearForm"
        @updateFormData="emit('updateFormData', $event)"
        @requestOperationsTypes="emit('requestOperationsTypes')"
        @requestCategories="emit('requestCategories')"
        @resetShowInputsErrors="emit('resetShowInputsErrors')"
        @formIsCleared="emit('formIsCleared')"
        @scrollTo="emit('scrollTo', $event)"
        @updateTransferTypeDocument="emit('updateTransferTypeDocument', $event)"
        @clearAllDocuments="emit('clearAllDocuments')"/>
        <!-- Amount Section -->
        <!-- Divider -->
        <divider-generic
        :dividerSize="'medium'"
        :icon="'bills'"
        :title="t('transfers.internationalCorporate.step1.dividers.amount')"/>
        <!-- Amount Inputs -->
        <transfer-amount-section
        :formData="formData"
        :showInputsErrors="showInputsErrors"
        :fieldsWithError="fieldsWithError"
        :clearForm="clearForm"
        @updateFormData="emit('updateFormData', $event)"
        @calculateEstimatedAmount="emit('calculateEstimatedAmount')"
        @formIsCleared="emit('formIsCleared')"
        @scrollTo="emit('scrollTo', $event)"/>
        <!-- Destination Section -->
        <!-- Divider -->
        <divider-generic
        :dividerSize="'medium'"
        :icon="'group'"
        :title="t('transfers.internationalCorporate.step1.dividers.destination')"/>
        <!-- Destination Tabs -->
        <tabs-segment :tabsList="tabsList" :class="{'mobile': getMobileAppState}" @clicked="emit('updateSelectedTab', $event)"/>
        <!-- Destination Inputs -->
        <transfer-destination-section
        :formData="formData"
        :isUSDCurrency="isUSDCurrency"
        :doesntComeWithCityInfo="doesntComeWithCityInfo"
        :doesntComeWithSwift="doesntComeWithSwift"
        :showInputsErrors="showInputsErrors"
        :fieldsWithError="fieldsWithError"
        :invalidIbanErrorMessage="invalidIbanErrorMessage"
        :invalidIbanError="invalidIbanError"
        :clearForm="clearTransferDestinationForm"
        :tabsList="tabsList"
        :ibanDetails="ibanDetails"
        :validatedIban="validatedIban"
        :unvalidatedSwift="unvalidatedSwift"
        :unvalidatedSwiftErrorMessage="unvalidatedSwiftErrorMessage"
        :applyOwnCustomerInfo="applyOwnCustomerInfo"
        @updateFormData="emit('updateFormData', $event)"
        @validateIban="emit('validateIban')"
        @validateSwiftCode="emit('validateSwiftCode')"
        @resetShowInputsErrors="emit('resetShowInputsErrors')"
        @formIsCleared="emit('formIsCleared')"
        @scrollTo="emit('scrollTo', $event)"/>
</template>

<script setup>
import InputGeneric from '@/components/generic/inputs/Generic'
import DividerGeneric from '@/components/generic/dividers/Generic'
import DropdownAccounts from '@/components/generic/dropdowns/accounts/Account'
import TabsSegment from '@/components/generic/tabs/Segment'
import OperationTypeSection from '@/components/features/transfers/internationalCorporate/OperationTypeSection'
import TransferAmountSection from '@/components/features/transfers/internationalCorporate/TransferAmountSection'
import TransferDestinationSection from '@/components/features/transfers/internationalCorporate/TransferDestinationSection'

/* Vue */
import { ref, computed, watch } from 'vue'

/* Assets */
import { emailValidator } from '@/assets/scripts/validators'
import { getElementOffsetTopPosition } from '@/assets/scripts/htmlManipulators'
import { getEmptyFieldsIdentifiers } from '@/assets/scripts/objects'

/* Store */
import { mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

const { getAccountsBalances } = mapGetters(moduleMapper.TRANSFERS_INTERNATIONAL_CORPORATE)
const { getMobileAppState } = mapGetters()

const emit = defineEmits([
    'openReuseTransferModal',
    'updateFormData',
    'updateSelectedTab',
    'calculateEstimatedAmount',
    'validateIban',
    'validateSwiftCode',
    'requestOperationsTypes',
    'requestCategories',
    'resetShowInputsErrors',
    'scrollTo',
    'formIsCleared',
    'updateTransferTypeDocument',
    'clearAllDocuments'
])

/** Props
@formData {Object} - Object with form data
@ibanDetails {Object} - Iban Details
@formTabsList {Array} - Tab List for Iban or Account Transfer
@validatedIban {Boolean} - Boolean controller show validated iban inputs
@showInputsErrors {Boolean} - Prop to trigger display of inputs errors
@unvalidatedSwift {Boolean} - Prop to trigger Swift input error
@unvalidatedSwiftErrorMessage {String} - Swift input error Message
@clearForm {Boolean} - Boolean controller to clear form and errors
@applyOwnCustomerInfo {Boolean} Boolean controller to show Own Customer Info
@fieldsWithError {Array} - Input List with error MDW provides to use
@invalidIbanError {Boolean} Boolean controller to apply iban error
@invalidIbanErrorMessage {String} Mdw Iban Error Message
*/
const props = defineProps({
    formData: Object,
    doesntComeWithCityInfo: Boolean,
    doesntComeWithSwift: Boolean,
    isUSDCurrency: Boolean,
    ibanDetails: Object,
    formTabsList: Array,
    validatedIban: Boolean,
	showInputsErrors: Boolean,
    unvalidatedSwift: Boolean,
	unvalidatedSwiftErrorMessage: String,
    applyOwnCustomerInfo: Boolean,
    clearForm: Boolean,
    fieldsWithError: Array,
    invalidIbanError: Boolean,
    invalidIbanErrorMessage: String
})

const clearTransferDestinationForm = ref(props.clearForm)

const filteredAccountsBalances = computed(() => {
    return getAccountsBalances.value.filter(obj => obj.currency === 'AKZ')
})

const tabsList = computed(() => {
   return props.formTabsList
})

const selectedTab = computed(() => {
    return tabsList.value.find(tab => tab.selected)
})

watch(() => selectedTab.value, () => {
    clearTransferDestinationForm.value = !clearTransferDestinationForm.value
})

/*************************************************
*                                                *
*              Origin Account Data               *
*                                                *
*************************************************/

const originAccountParams = ref({
    labelText: t('transfers.internationalCorporate.genericLabels.account')
})

const emailInputParams = ref({
	labelText: t('transfers.internationalCorporate.genericLabels.ownEmail'),
    placeholder: t('transfers.internationalCorporate.step1.placeholders.ownEmail'),
    mandatory: true,
    maxLength: 42,
	errorState: false,
    errorMessage: null
})

const emailValidation = () => {
    if (!!props.formData.ownEmail && !emailValidator(props.formData.ownEmail)) {
        emailInputParams.value.errorState = true
        emailInputParams.value.errorMessage = t('invalidEmailError')

        emit('scrollTo', getElementOffsetTopPosition('ownEmail'))
    }
}

const originAccountDropdownClickHandler = event => {
    const accountDisplayInfo = filteredAccountsBalances.value.find(account => account.id === event).accountNumber

    emit('updateFormData', { field: 'accountDisplayInfo', inputValue: accountDisplayInfo })
    emit('updateFormData', { field: 'originAccount', inputValue: event })
}

/**********************************************************************
*                                                                     *
*                          Mandatory Fields                           *
*                                                                     *
***********************************************************************/
const step1MandatoryList = ref([
    'ownEmail'
])

const fieldsWithErrorMessage = ref({
    ownEmail: emailInputParams.value
})

const showInputsErrorsHandler = () => {
	/* Logic to scroll to first emptyField */
	const mandatoryFieldsEmptyList = getEmptyFieldsIdentifiers(props.formData, step1MandatoryList.value)

    if (mandatoryFieldsEmptyList.length) {
        mandatoryFieldsEmptyList.forEach(fieldName => {
            fieldsWithErrorMessage.value[fieldName].errorMessage = t('mandatoryFieldError')
            fieldsWithErrorMessage.value[fieldName].errorState = true
        })

        emit('scrollTo', getElementOffsetTopPosition(mandatoryFieldsEmptyList[0]), 'showInputsErrorsHandler')
    }

    if (props.fieldsWithError.length) {
        props.fieldsWithError.forEach(field => {
            if (fieldsWithErrorMessage.value[field.parsedFieldOnError]) {
                fieldsWithErrorMessage.value[field.parsedFieldOnError].errorState = true
                fieldsWithErrorMessage.value[field.parsedFieldOnError].errorMessage = field.errorMessage
            }
        })

        emit('scrollTo', getElementOffsetTopPosition(props.fieldsWithError[0]))
    }
}

watch(() => props.showInputsErrors, newVal => {
	if (newVal) showInputsErrorsHandler()
	emit('resetShowInputsErrors')
})

/**********************************************************************
*                                                                     *
*                           Clear Field Errors                        *
*                                                                     *
***********************************************************************/

const clearFieldError = (fieldName) => {
    fieldsWithErrorMessage.value[fieldName].errorMessage = null
    fieldsWithErrorMessage.value[fieldName].errorState = false
}

const clearFormErrors = () => {
	for (var fieldName in fieldsWithErrorMessage.value) {
		clearFieldError(fieldName)
	}
}

watch(() => props.clearForm, () => {
	clearFormErrors()
	emit('formIsCleared')
})
</script>

<style lang="scss" scoped>
.reuse-transfer-button {
    align-self: center;
    margin-bottom: 24px;
}

.tabs-segment-wrapper {
    align-self: center;

    :deep(.tab) {
        min-width: 250px;
    }

    &.mobile {
        width: 100%;

        :deep(.tab) {
            width: calc(100% / 2);
            min-width: fit-content;
        }
    }
}
</style>
